import React, { useEffect, useState, useCallback, useRef } from "react";
import { CircularProgress } from "@mui/material";
import { useMsal } from "@azure/msal-react";
import CustomiseColumns from "Samarth Module/components/drawers/CustomiseColumns";
import MoreFilters from "Samarth Module/components/drawers/moreFilters";
import MultiSelectWithSearchInput from "Samarth Module/components/dropdown/MultiSelectWithInput";
import OverflowMenu from "Samarth Module/components/overflowMenu/OverflowMenu";
import styles from "./POPriority.module.css";
import Filters from "./../../../../assets/images/filters.svg";
import Refresh from './../../../../assets/images/ArrowCounterClockwise.svg';
import Threedots from './../../../../assets/images/PhosphorLine.svg';
import DispatchTable from "../dispatchPriority/DispatchTable";
import POWisePriority from "./POWisePriority";
import POValidityBucket from "./POValidityBucket";
import {
    fetchPoPriorityFilters, fetchPOValidityBuckets, fetchPOPriorityOverviewTable,
    fetchDCLevelPOWisePriorityStatus, fetchCustomerLevelPOWisePriorityStatus, handleExportPOPriorityOverview
} from "Samarth Module/api/Qcommapi";
import SnackBarMessage from "Samarth Module/components/snackbar/snackbar";

const POPriority = ({ sideTab, handleFilterSelection }) => {
    const { accounts } = useMsal();
    const activeAccount = accounts[0];
    const [poPriorityData, setPoPriorityData] = useState({});
    const [loading, setLoading] = useState({
        initialLoading: true,
        priorityStatus: false,
        validityBuckets: false,
        overviewTableLoading: false,
        exportLoading: false,
    });
    const [isDrawerOpen, setIsDrawerOpen] = useState({
        filterDrawer: false,
        customiseDrawer: false,
    });
    const [filters, setFilters] = useState({
        poPriorityFiltersData: [],
        defaultColsOverviewTable: [],
    });
    const [sortingData, setSortingData] = useState({
        sortCol: null,
        sortDirection: null,
    });
    const [snackBarObject, setSnackBarObject] = useState({
        snackbarOpen: false,
        message: '',
        type: 'success',
    });
    const clearFiltersRef = useRef(false);

    const fetchInitialData = async (poPriorityFilters = []) => {
        const shouldClearFilters = clearFiltersRef.current;
        clearFiltersRef.current = false;
        
        try {
            const payload = {
                email: activeAccount.username,
                "uom": sideTab || 'Tonnes',
                filters: poPriorityFilters.reduce((acc, filter) => {
                    acc[filter.label] = filter.selectedData;
                    return acc;
                }, {}),
                "type": "PO Priority",
                clearFilters: shouldClearFilters,
            };

            const filtersData = await fetchPoPriorityFilters(payload);

            setFilters({
                defaultColsOverviewTable: filtersData['defaultColumns'] || [],
                poPriorityFiltersData: filtersData['filters'] || [],
            });

            fetchData(filtersData['filters'] || []);
        } catch (error) {
            console.error("Error fetching PO Priority data:", error);
        } finally {
            setLoading(prevState => ({ ...prevState, initialLoading: false }));
        }
    };

    const fetchData = async (poPriorityFilters = []) => {
        setLoading(prevState => ({
            ...prevState,
            priorityStatus: true,
            validityBuckets: true,
            overviewTableLoading: true
        }));
        try {
            const payload = {
                email: activeAccount.username,
                "uom": sideTab || 'Tonnes',
                filters: poPriorityFilters.reduce((acc, filter) => {
                    acc[filter.label] = filter.selectedData;
                    return acc;
                }, {}),
                "type": "PO Priority",
            };
            const [poValidityBucketData, customerLevelPOWisePriorityStatusData, dcLevelPOWisePriorityStatusData, overViewData] = await Promise.all([
                fetchPOValidityBuckets(payload),
                fetchCustomerLevelPOWisePriorityStatus(payload),
                fetchDCLevelPOWisePriorityStatus(payload),
                fetchPOPriorityOverviewTable({ ...payload, page: 1, pageSize: poPriorityData?.['paginationData']?.['rowsPerPage'] || 15 }),
            ]);
            setPoPriorityData({
                overviewTableData: overViewData['data'] || [],
                paginationData: overViewData?.['pagination'] || {},
                poPriorityCustomerData: customerLevelPOWisePriorityStatusData['graph_data'] || [],
                poPriorityDCData: dcLevelPOWisePriorityStatusData['graph_data'] || [],
                poValidityBuckets: poValidityBucketData['graph_data'] || [],
            });
        } catch (error) {
            console.error("Error fetching PO Priority data:", error);
        } finally {
            setLoading(prevState => ({
                ...prevState,
                initialLoading: false,
                priorityStatus: false,
                validityBuckets: false,
                overviewTableLoading: false
            }));
        }
    };

    useEffect(() => {
        if (window.location.pathname === '/samarth/po_priority') {
            fetchInitialData();
        }
    }, []);

    // Fetch data when filters or sideTab changes
    useEffect(() => {
        if (!loading.initialLoading && filters.poPriorityFiltersData.length > 0) {
            fetchData();
        }
    }, [sideTab]);

    const overTableData = async (rowsPerPage, newPage, sortCol, sortDirection) => {
        // setLoading(prev => ({
        //     ...prev,
        //     "overviewTableLoading": true,
        // }));
        try {
            const payload = {
                email: activeAccount.username,
                "uom": sideTab || 'Tonnes',
                filters: filters["poPriorityFiltersData"].reduce((acc, filter) => {
                    acc[filter.label] = filter.selectedData;
                    return acc;
                }, {}),
                "type": "PO Priority",
                "page": newPage,
                "pageSize": rowsPerPage,
            };
            if (sortCol && sortDirection) {
                payload.sortBy = sortCol;
                payload.sortOrder = sortDirection;
            }
            const overviewTableData = await fetchPOPriorityOverviewTable(payload);
            setPoPriorityData(prev => ({
                ...prev,
                overviewTableData: overviewTableData["data"] || [],
                paginationData: overviewTableData["pagination"] || null
            }));
        } catch (error) {
            setPoPriorityData(prev => ({
                ...prev,
                overviewTableData: [],
                paginationData: prev.paginationData || null,
            }));
            console.error("Error fetching overview table data:", error);
        } finally {
            // setLoading(prev => ({
            //     ...prev,
            //     "overviewTableLoading": false,
            // }));
        }
    };

    const toggleFilterDrawer = () => {
        setIsDrawerOpen(prevState => ({
            ...prevState,
            filterDrawer: !prevState.filterDrawer
        }));
    };

    const toggleCustomizeDrawer = () => {
        setIsDrawerOpen(prevState => ({
            ...prevState,
            customiseDrawer: !prevState.customiseDrawer
        }));
    };

    const handleFilterChange = (filterLabel, selectedData) => {
        let newFilter;
        setFilters(prevFilters => {
            newFilter = {
                ...prevFilters,
                poPriorityFiltersData: prevFilters["poPriorityFiltersData"].map(filter =>
                    filter.label === filterLabel
                        ? { ...filter, selectedData: selectedData }
                        : filter
                )
            }
            return newFilter;
        });
        handleFilterSelection("PO Priority", filterLabel, selectedData);
        
        // Check if there are any selected filters
        const hasSelection = newFilter["poPriorityFiltersData"].some((filter) => {
            const selected = filter?.selectedData;
            if (Array.isArray(selected)) return selected.length > 0;
            if (typeof selected === 'string') return selected.trim().length > 0;
            return selected !== undefined && selected !== null;
        });
        
        clearFiltersRef.current = !hasSelection;
        setSortingData({ sortCol: null, sortDirection: null });
        fetchInitialData(newFilter['poPriorityFiltersData']);

    };

    const resetFilter = () => {
        clearFiltersRef.current = true;
        let newFilter;
        setFilters(prevFilters => {
            const resetFilters = prevFilters.poPriorityFiltersData.map(filter => ({
                ...filter,
                selectedData: []
            }));
            newFilter = {
                ...prevFilters,
                poPriorityFiltersData: resetFilters
            };
            return newFilter;
        });
        setSortingData({ sortCol: null, sortDirection: null });
        fetchInitialData(newFilter['poPriorityFiltersData']);
    };

    const handleOverviewExport = async () => {
        setLoading(prev => ({
            ...prev,
            "exportLoading": true,
        }));
        try {
            const payload = {
                email: activeAccount.username,
                "uom": sideTab || 'Tonnes',
                filters: filters["poPriorityFiltersData"].reduce((acc, filter) => {
                    acc[filter.label] = filter.selectedData;
                    return acc;
                }, {}),
                "type": "PO Priority",
            };
            await handleExportPOPriorityOverview(payload, accounts[0].idToken);
            setSnackBarObject({ snackbarOpen: true, message: "File exported successfully", type: 'success' });
        } catch (error) {
            console.error("Error exporting data:", error);
            setSnackBarObject({ snackbarOpen: true, message: "Error occured while exporting file. Please try again later.", type: 'error' });
        } finally {
            setLoading(prev => ({
                ...prev,
                "exportLoading": false,
            }));
        }
    };

    const closeSnackbar = useCallback(() => {
        setSnackBarObject((prev) => ({ ...prev, snackbarOpen: false }));
    }, []);

    return (
        <>

            {loading.initialLoading ?
                <div className={styles.loaderContainer}><CircularProgress /></div> :
                <div className={`${styles.container} ${loading.exportLoading ? styles.blurBackground : ''}`}>
                    <div className={styles.section}>
                        {filters.poPriorityFiltersData.filter((filter) => filter.isFavourite).map((filter, index) => (
                            <div key={`${filter.label}-${index}`} className={styles.filterWrapper}>
                                <MultiSelectWithSearchInput
                                    label={filter.label}
                                    options={filter.data}
                                    filterValues={filter.selectedData}
                                    isMultiSelect={filter.isMultiSelect}
                                    handleFilter={handleFilterChange}
                                    type={'PO Priority'}
                                />
                            </div>
                        ))}
                        <div className={styles.iconContainer}>
                            <img src={Refresh} className={styles.iconStyling} alt="refresh filter" onClick={resetFilter} />
                            <OverflowMenu
                                menuItems={[
                                    {
                                        label: 'More Filters',
                                        icon: Filters,
                                        onClick: toggleFilterDrawer,
                                    }
                                ]}
                                Icon={Threedots}
                            />
                        </div>
                    </div>
                    <div className={styles.middlePageContainer}>
                        <div className={styles.halfSection}>
                            {loading["priorityStatus"] ?
                                <div className={styles.loaderContainer}><CircularProgress /></div> :
                                <POWisePriority label={"PO Wise Priority Status"} customerData={poPriorityData['poPriorityCustomerData']} dcData={poPriorityData['poPriorityDCData']} />
                            }
                        </div>
                        <div className={styles.halfSection}>
                            {loading["validityBuckets"] ?
                                <div className={styles.loaderContainer}><CircularProgress /></div> :
                                <POValidityBucket label={"PO Validity Buckets"} data={poPriorityData['poValidityBuckets']} />
                            }
                        </div>
                    </div>
                    <div className={styles.overViewTableContainer}>
                        {loading["overviewTableLoading"] ?
                            <div className={styles.loaderContainer}><CircularProgress /></div> :
                            <DispatchTable
                                tableData={poPriorityData["overviewTableData"]}
                                paginationData={poPriorityData["paginationData"]}
                                label={"Overview"}
                                columns={filters["defaultColsOverviewTable"]}
                                handleOpenCustomization={toggleCustomizeDrawer}
                                sortingData={sortingData}
                                handlePageChange={(rowsPerPage, newPage, sortCol, sortDirection) => {
                                    if (sortCol && sortDirection) {
                                        setSortingData({ sortCol, sortDirection });
                                    }
                                    overTableData(rowsPerPage, newPage, sortCol, sortDirection);
                                }}
                                handleExport={handleOverviewExport}
                            />
                        }
                    </div>
                    {isDrawerOpen.filterDrawer &&
                        <MoreFilters
                            anchor={'right'}
                            filterDrawer={isDrawerOpen.filterDrawer}
                            handleFilterDrawer={toggleFilterDrawer}
                            rmpmFilters={filters["poPriorityFiltersData"]}
                            setRmpmFilters={(filters) => {
                                setFilters(prevFilters => ({
                                    ...prevFilters,
                                    poPriorityFiltersData: filters
                                }));
                            }}
                            username={activeAccount.username}
                            type={"PO Priority"}
                            limit={9}
                        />
                    }
                    {isDrawerOpen["customiseDrawer"] &&
                        <CustomiseColumns
                            anchor={'right'}
                            columns={[...filters["defaultColsOverviewTable"]]}
                            isOpen={isDrawerOpen["customiseDrawer"]}
                            handleCustomizeDrawer={toggleCustomizeDrawer}
                            columnCustomization={(customizedColumns) => {
                                setFilters(prevFilters => ({
                                    ...prevFilters,
                                    poPriorityFiltersData: customizedColumns
                                }));
                                toggleCustomizeDrawer();
                            }}
                            username={activeAccount.username}
                            type={"PO Priority"}
                        />
                    }
                </div>
            }
            {loading["exportLoading"] && <div className={styles.loaderExcelContainer}><CircularProgress /></div>}
            {snackBarObject.snackbarOpen && (
                    <SnackBarMessage
                        type={snackBarObject.type}
                        snackbarMessage={snackBarObject.message}
                        snackbarOpen={snackBarObject.snackbarOpen}
                        setSnackbarOpen={closeSnackbar}
                    />
                )
            }
        </>
    );
};

export default POPriority;